import objectPath from 'object-path';
import { PathCondition } from '../settings/settings';

export interface Includable {
    /**
     * A function to check if a value is included in an object
     * @param T A value to check if it is included
     */
    includes(T);
}

export function testCondition<T extends Includable>({ operator, value }: PathCondition<T>, valueToTest: T): boolean {
    switch (operator) {
        case 'equal':
        case 'eq':
            return valueToTest === value;
        case 'contains':
            return valueToTest.includes(value);
        case 'not-equal':
        case 'notEqual':
        case 'ne':
            return valueToTest !== value;

        default:
            return false;
    }
}

export function testPathCondition<T>(condition: PathCondition<T>, object: T): boolean {
    const conditionPath = condition.path || condition.statePath;
    const valueAtPath = objectPath.get(object, conditionPath);
    const result = testCondition(condition, valueAtPath);
    return result;
}

/**
 * Return whether any of the conditions are met by the object
 */
export function hasSomeValueAtPaths<T>(conditions: Array<PathCondition<T>>, object: T): boolean {
    if (!conditions.length) {
        return false;
    }

    const finalResult = conditions.reduce((result, condition) => {
        // This is an OR so we can bail in this case
        if (result) {
            return result;
        }

        const currentResult = testPathCondition(condition, object);
        return result || currentResult;
    }, false);
    return finalResult;
}

/**
 * Return whether all of the conditions are met by the object
 */
export function hasAllValuesAtPaths<T>(conditions: Array<PathCondition<T>>, object: T): boolean {
    if (!conditions.length) {
        return true;
    }

    const finalResult = conditions.reduce((result, condition) => {
        // This is an AND so we can bail in this case
        if (!result) {
            return result;
        }

        const currentResult = testPathCondition(condition, object);
        return result && currentResult;
    }, true);
    return finalResult;
}

/**
 * Return whether any complete lists of conditions are met by the object
 */
export function hasAnySetsAtPaths<T>(conditionLists: Array<Array<PathCondition<T>>>, object: T): boolean {
    if (!conditionLists.length) {
        return false;
    }

    const finalResult = conditionLists.reduce((result, conditions) => {
        // This is an OR so we can bail in this case
        if (result) {
            return result;
        }

        const currentResult = hasAllValuesAtPaths(conditions, object);
        return result || currentResult;
    }, false);
    return finalResult;
}



// WEBPACK FOOTER //
// ./node_modules/@alme/ui-web/util/test-object-condition.ts